import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import api from "../../services/api.js";

export default function UpdateCustomerModal({
  open,
  onClose,
  token,
  customer,
  onSuccess,
}) {
  const [form, setForm] = useState({});
  const [customerGroups, setCustomerGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && customer) {
      setForm({
        full_name: customer.full_name || "",
        customer_id: customer.customer_id || "",
        email: customer.email || "",
        phone_number: customer.phone_number || "",
        residence_type: customer.residence_type || "",
        address: customer.address || "",
        city: customer.city || "",
        state: customer.state || "",
        identity_type: customer.identity_type || "",
        identity_number: customer.identity_number || "",
        referred_by: customer.referred_by || "",
        status: customer.status || "active",
        password: "",
      });
    }
  }, [open, customer]);

  useEffect(() => {
    const fetchCustomerGroups = async () => {
      if (!open) return;

      try {
        const res = await api.get("/customer-groups", {
          headers: {
            Authorization: token,
          },
        });

        setCustomerGroups(res.data.data || []);
      } catch (err) {
        console.error("Gagal load customer groups:", err);
        setCustomerGroups([]);
      }
    };

    fetchCustomerGroups();
  }, [open, token]);

  if (!open || !customer) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {};

    Object.keys(form).forEach((key) => {
      if (key === "password") {
        if (form.password.trim()) {
          payload.password = form.password;
        }
        return;
      }

      if (form[key] !== customer[key]) {
        payload[key] = form[key];
      }
    });

    if (Object.keys(payload).length === 0) {
      Swal.fire("Info", "Tidak ada perubahan data", "info");
      return;
    }

    setLoading(true);

    try {
      const res = await api.put(`/customers/${customer.id}`, payload, {
        headers: {
          Authorization: token,
        },
      });

      Swal.fire("Berhasil", res.data.message, "success");

      onSuccess?.();
      onClose();
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.message || err.message || "Terjadi kesalahan",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/70">
      <div className="w-full max-w-xl rounded-xl bg-white dark:bg-gray-900 p-6 shadow-lg">
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Update Customer
        </h2>

        <form onSubmit={handleSubmit} className="space-y-3">
          <Input
            name="full_name"
            value={form.full_name || ""}
            onChange={handleChange}
            placeholder="Nama Lengkap"
          />

          <Input
            name="customer_id"
            value={form.customer_id || ""}
            onChange={handleChange}
            placeholder="Customer ID"
          />

          <Input
            type="password"
            name="password"
            value={form.password || ""}
            onChange={handleChange}
            placeholder="Password Baru (opsional)"
          />

          <Input
            type="email"
            name="email"
            value={form.email || ""}
            onChange={handleChange}
            placeholder="Email"
          />

          <Input
            name="phone_number"
            value={form.phone_number || ""}
            onChange={handleChange}
            placeholder="No. Telepon"
          />

          <Select
            name="residence_type"
            value={form.residence_type || ""}
            onChange={handleChange}
          >
            <option value="">Pilih Jenis Hunian</option>
            <option value="rumah pribadi">Rumah Pribadi</option>
            <option value="rumah kontrakan">Rumah Kontrakan</option>
            <option value="cafe">Cafe</option>
            <option value="warung">Warung</option>
            <option value="toko">Toko</option>
            <option value="kantor">Kantor</option>
            <option value="sekolah">Sekolah</option>
            <option value="kantor pemerintahan">Kantor Pemerintahan</option>
          </Select>

          <Textarea
            name="address"
            value={form.address || ""}
            onChange={handleChange}
            placeholder="Alamat"
          />

          <Input
            name="city"
            value={form.city || ""}
            onChange={handleChange}
            placeholder="City"
          />

          <Input
            name="state"
            value={form.state || ""}
            onChange={handleChange}
            placeholder="State"
          />

          <Select
            name="identity_type"
            value={form.identity_type || ""}
            onChange={handleChange}
          >
            <option value="">Pilih Identitas</option>
            <option value="KTP">KTP</option>
            <option value="SIM">SIM</option>
          </Select>

          <Input
            name="identity_number"
            value={form.identity_number || ""}
            onChange={handleChange}
            placeholder="Nomor Identitas"
          />

          <Select
            name="referred_by"
            value={form.referred_by || ""}
            onChange={handleChange}
          >
            <option value="">Pilih Customer Group</option>

            {customerGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.groupname}
              </option>
            ))}
          </Select>

          <Select
            name="status"
            value={form.status || "active"}
            onChange={handleChange}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="closed">Closed</option>
          </Select>

          <div className="flex justify-end gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Batal
            </button>

            <button
              type="submit"
              disabled={loading}
              className="rounded-md bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Menyimpan..." : "Update"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* Reusable Inputs */
const base =
  "w-full rounded-md border px-3 py-2 outline-none " +
  "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 " +
  "border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-blue-500";

const Input = (props) => <input {...props} className={base} />;

const Textarea = (props) => <textarea {...props} className={base} />;

const Select = (props) => <select {...props} className={base} />;
